import {
  CheckListControlled,
  EmptyState,
  OverflowSidebarContentWithTabs,
  SearchInput,
} from 'components';
import { useFilter } from 'hooks';
import React, { FC, useCallback, useMemo, useRef } from 'react';
import { ControllerRenderProps } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getEventTasks } from 'store/selectors/eventTasks';

import { EventFormData } from '../form';
import { StyledSearchWrapper } from './styled';

interface CreateEventTasksProps {
  tasksFieldProps: ControllerRenderProps<EventFormData>;
}

const CreateEventTasks: FC<CreateEventTasksProps> = ({ tasksFieldProps }) => {
  const { t } = useTranslation();
  const tasks = useSelector(getEventTasks);
  const searchInputRef = useRef<HTMLDivElement>(null);
  const {
    filteredItems: filteredTasks,
    searchInputValue,
    handleChangeSearch,
  } = useFilter(tasks);

  const allChecked = useMemo(() => {
    return filteredTasks.every((t) => tasksFieldProps?.value?.includes(t.id));
  }, [tasksFieldProps, filteredTasks]);

  const handleSelectAllClick = useCallback(() => {
    if (allChecked) {
      tasksFieldProps.onChange([]);
    } else {
      tasksFieldProps.onChange(filteredTasks.map((t) => t.id));
    }
  }, [allChecked, tasksFieldProps, filteredTasks]);

  return (
    <OverflowSidebarContentWithTabs>
      <StyledSearchWrapper ref={searchInputRef}>
        <SearchInput
          width="100%"
          value={searchInputValue}
          onChange={({ target }) => handleChangeSearch(target.value)}
        />
      </StyledSearchWrapper>
      {filteredTasks.length > 0 ? (
        <CheckListControlled
          search={searchInputValue}
          items={filteredTasks}
          label={t('common.tasks')}
          selectedItems={tasksFieldProps.value}
          selectItem={(t) => {
            tasksFieldProps.onChange(
              tasksFieldProps.value.includes(t)
                ? tasksFieldProps.value.filter((i) => i !== t)
                : [...tasksFieldProps.value, t],
            );
          }}
          handleSelectAllClick={handleSelectAllClick}
          allChecked={allChecked}
        />
      ) : searchInputValue.trim() ? (
        <EmptyState
          icon="🔍"
          title={t('event.noSearchResults.title')}
          description={t('event.noSearchResults.description', {
            searchTerm: `"${searchInputValue.trim()}"`,
          }).replace(/&quot;/g, '"')}
          onClearSearch={() => {
            handleChangeSearch('');
            if (searchInputRef.current) {
              const input = searchInputRef.current.querySelector('input');
              if (input) {
                input.value = '';
                input.dispatchEvent(new Event('input', { bubbles: true }));
              }
            }
          }}
        />
      ) : (
        <EmptyState
          icon="📋"
          title={t('event.noTasks.title')}
          description={t('event.noTasks.description')}
        />
      )}
    </OverflowSidebarContentWithTabs>
  );
};

export default CreateEventTasks;
